import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Check, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import Header from "@/components/public/Header";
import Footer from "@/components/public/Footer";
import { useLead } from "@/components/public/LeadDialog";
import { Picture } from "@/components/public/Picture";
import ContentSections from "@/components/public/ContentSections";
import { useVirtualTour, TourOverlay } from "@/components/public/VirtualTour";

export default function LocationDetail() {
  const { slug } = useParams();
  useSeo(`/locations/${slug}`);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({});
  const [notFound, setNotFound] = useState(false);
  const { openLead } = useLead();
  const vt = useVirtualTour();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/public/locations/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
    api.get("/public/homepage").then((r) => setSettings(r.data.settings));
  }, [slug]);

  if (notFound) return <NotFound label="Location" />;
  if (!data) return <Spinner />;
  const { location: l, workspaces } = data;

  return (
    <div className="bg-white min-h-screen">
      <Header phone={settings.phone} />
      <nav className="container-cz pt-6 flex items-center gap-1.5 text-sm text-slate-500" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-primary">Home</Link><ChevronRight className="w-3.5 h-3.5 text-slate-300" />
        <Link to="/locations" className="hover:text-primary">Locations</Link><ChevronRight className="w-3.5 h-3.5 text-slate-300" />
        <span className="text-slate-900 font-medium">{l.name}</span>
      </nav>

      <div className="container-cz pt-8 pb-12 grid lg:grid-cols-2 gap-10 items-start">
        <div className="relative rounded-3xl overflow-hidden shadow-cz aspect-[4/3]">
          <Picture src={l.image} alt={l.image_alt || `${l.name}, ${l.sector}, Noida`} loading="eager" className="w-full h-full object-cover" />
          {l.virtual_tour?.available && (
            <TourOverlay onClick={() => vt.openTour({
              location_slug: l.slug, name: l.name, sector: l.sector,
              preview_image: l.virtual_tour.preview_image || l.image,
            })} />
          )}
        </div>
        <div>
          <p className="text-sm text-primary font-medium flex items-center gap-1"><MapPin className="w-4 h-4" /> {l.sector}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 mt-2">{l.name}</h1>
          <p className="text-lg text-slate-600 mt-4">{l.description}</p>
          <p className="text-sm text-slate-500 mt-4 flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /> {l.address}</p>
          <p className="text-sm text-slate-500 mt-1.5 flex items-center gap-2"><Phone className="w-4 h-4" /> {l.phone}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Button onClick={() => openLead("tour", { location: l.slug, city: (l.sector || "").split(",").pop().trim() })} data-testid="loc-book-tour" className="rounded-full h-12 px-7 gap-2">Book a Tour <ArrowRight className="w-4 h-4" /></Button>
            <Button onClick={() => openLead("quote", { location: l.slug, city: (l.sector || "").split(",").pop().trim() })} data-testid="loc-get-quote" variant="outline" className="rounded-full h-12 px-7 border-slate-300">Get a Quote</Button>
          </div>
        </div>
      </div>

      {l.intro && (
        <div className="container-cz pb-8 max-w-3xl">
          <p className="text-slate-600 text-lg leading-relaxed whitespace-pre-line" data-testid="loc-intro">{l.intro}</p>
        </div>
      )}

      <div className="container-cz pb-12">
        <h2 className="font-display text-2xl font-bold text-slate-900 mb-5">Key Information & Amenities</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(l.amenities || []).map((a) => (
            <div key={a} className="flex items-center gap-2.5 card-cz p-4">
              <span className="w-7 h-7 rounded-full bg-sky-100 text-primary flex items-center justify-center shrink-0"><Check className="w-4 h-4" /></span>
              <span className="text-sm font-medium text-slate-700">{a}</span>
            </div>
          ))}
        </div>
      </div>

      {(l.gallery || []).length > 1 && (
        <div className="container-cz pb-12" data-testid="location-gallery">
          <h2 className="font-display text-2xl font-bold text-slate-900 mb-5">Photo Gallery</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {l.gallery.slice(1).map((g, i) => (
              <a key={i} href={g.src} target="_blank" rel="noopener noreferrer" data-testid={`gallery-img-${i}`}
                className="group block aspect-[4/3] rounded-xl overflow-hidden bg-slate-100">
                <Picture src={g.src} alt={g.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </a>
            ))}
          </div>
        </div>
      )}

      {workspaces?.length > 0 && (
        <div className="container-cz pb-20">
          <h2 className="font-display text-2xl font-bold text-slate-900 mb-5">Workspaces Available Here</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {workspaces.map((w) => (
              <button key={w.id} onClick={() => navigate(`/workspaces/${w.slug}`)} className="group card-cz overflow-hidden text-left hover:shadow-cz transition-all">
                <div className="aspect-[16/10] overflow-hidden"><Picture src={w.image} alt={w.image_alt || `${w.title} at ${l.name}, Noida`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-4"><h3 className="font-semibold text-slate-900">{w.title}</h3><p className="text-sm text-slate-500 mt-1 line-clamp-2">{w.description}</p></div>
              </button>
            ))}
          </div>
        </div>
      )}
      <ContentSections sections={l.content_sections} />
      <Footer settings={settings} />
    </div>
  );
}

export function Spinner() {
  return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
}
export function NotFound({ label }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-50">
      <h1 className="font-display text-3xl font-bold text-slate-900">{label} not found</h1>
      <Link to="/" className="text-primary font-semibold">← Back to home</Link>
    </div>
  );
}
